/** Backoff before each automatic retry of a failed dynamic import; its length is the retry cap. */
export const IMPORT_RETRY_DELAYS_MS: readonly number[] = [800, 2400];

const NETWORK_FAILURE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading (css )?chunk|failed to fetch|load failed|networkerror/i;

/**
 * Whether a dynamic `import()` rejection looks like the chunk could not be FETCHED (offline, 404 on
 * a stale deploy, a blocked request) rather than a bug in it. Only the former is worth retrying.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === "ChunkLoadError" || NETWORK_FAILURE.test(error.message);
}

const MODULE_URL = /(?:failed to fetch|error loading) dynamically imported module:\s*(\S+)/i;

/** The module URL a browser names in its dynamic-import failure message, if it does (Chromium, Firefox). */
export function failedModuleUrl(error: unknown): string | null {
  return error instanceof Error ? (MODULE_URL.exec(error.message)?.[1] ?? null) : null;
}

/** `url` with a per-attempt query, so the browser treats it as a module it has not failed on yet. */
export function cacheBustedUrl(url: string, attempt: number | string): string {
  const base = url.replace(/([?&])wexio-retry=[\w-]+&?/, "$1").replace(/[?&]$/, "");
  return `${base}${base.includes("?") ? "&" : "?"}wexio-retry=${attempt}`;
}

/** The imported module is not the one that was asked for (a dependency chunk, a stale build). */
function wrongShape(): Error {
  const error = new Error("Failed to load the chunk: the module does not have the expected export");
  error.name = "ChunkLoadError";
  return error;
}

/**
 * Run `load` (a function, so every attempt is a NEW `import()` and not the cached rejection),
 * retrying network failures after each delay in `delays`. A real error is not retried. Rejects with
 * the last error once the retries are used up.
 *
 * A browser remembers a failed dynamic import per URL for the life of the document, so repeating the
 * same `import()` fails again without touching the network. When the failure names its module and a
 * `reimport` is given, the retry imports that module under a cache-busting URL instead. The named
 * module may be a shared DEPENDENCY chunk rather than the entry: if the re-imported module does not
 * pass `accept` it is not the entry, so the entry is imported again afterwards (the dependency has
 * been re-fetched by then). `accept` also guards `load` itself: a module without what the caller
 * needs counts as a chunk error and is retried like any other.
 */
export async function retryImport<T>(
  load: () => Promise<T>,
  delays: readonly number[] = IMPORT_RETRY_DELAYS_MS,
  reimport?: (url: string, attempt: number) => Promise<T>,
  accept: (module: T) => boolean = () => true,
): Promise<T> {
  const checked = async (): Promise<T> => {
    const module = await load();
    if (!accept(module)) throw wrongShape();
    return module;
  };
  let run = checked;
  for (let attempt = 0; ; attempt++) {
    try {
      return await run();
    } catch (error) {
      const delay = delays[attempt];
      if (delay === undefined || !isChunkLoadError(error)) throw error;
      const url = reimport ? failedModuleUrl(error) : null;
      const next = attempt + 1;
      if (url && reimport) {
        run = async () => {
          const module = await reimport(url, next);
          return accept(module) ? module : checked();
        };
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}
